import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import { acuerdoDe, describeAcuerdo, setAcuerdoComision, comisionesDePlanner, TIPOS_COMISION } from "../../utils/comisiones";
import { formatCalendarDate } from "../../utils/dates";
import HistorialPrecios from "../shared/HistorialPrecios";
import { Badge } from "../badge";
import { PencilSquareIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";
const fmtMoney = (v) => "$" + (Number(v) || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 });
const inputClass =
  "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

// Acuerdo de comisión del planner (FRA-355) + sus eventos con la comisión
// de cada uno y si ya se pagó. El acuerdo lleva historial: cambiarlo no
// re-afecta eventos anteriores (cada costo es un snapshot) y el historial
// explica por qué un evento de hace ocho meses pagó distinto.
export default function PlannerComisionSection({ proveedor, soloLectura = false }) {
  const navigate = useNavigate();
  const { selectedVenueId, costos, events, calendarTimezone } = useVenue();
  const { user, userInfo } = useAuth();
  const acuerdo = acuerdoDe(proveedor);
  const [editando, setEditando] = useState(false);
  const [tipo, setTipo] = useState(acuerdo?.tipo || "porcentaje");
  const [valor, setValor] = useState(acuerdo ? String(acuerdo.valor) : "");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  const eventsById = useMemo(() => {
    const m = {};
    (events || []).forEach((e) => { m[e.id] = e; });
    return m;
  }, [events]);
  const comisiones = useMemo(
    () =>
      comisionesDePlanner(costos, proveedor?.id)
        .map((c) => ({ costo: c, evento: eventsById[c.eventId] || null }))
        .sort((a, b) => (b.costo.fecha?.seconds || 0) - (a.costo.fecha?.seconds || 0)),
    [costos, proveedor?.id, eventsById]
  );
  const pagadas = comisiones.filter((c) => c.costo.estado === "pagado").reduce((s, c) => s + (Number(c.costo.monto) || 0), 0);
  const porPagar = comisiones.filter((c) => (c.costo.estado || "por_pagar") !== "pagado").reduce((s, c) => s + (Number(c.costo.monto) || 0), 0);
  // Eventos donde es planner pero sin comisión registrada (asignado antes de
  // esta feature, o sin acuerdo en su momento).
  const sinComision = useMemo(() => {
    const con = new Set(comisiones.map((c) => c.costo.eventId));
    return (events || []).filter((e) => e.plannerId === proveedor?.id && !con.has(e.id) && e.status !== "cancelado");
  }, [events, comisiones, proveedor?.id]);

  const guardar = async () => {
    const v = Number(normalizeMoneyInput(valor));
    if (!(v >= 0)) {
      showAlert("Ingresa un valor válido.", "error");
      return;
    }
    setSaving(true);
    try {
      const nuevo = await setAcuerdoComision({ venueId: selectedVenueId, proveedor, tipo, valor: v, notas: notas.trim() || null, user, userInfo });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_COMISION_UPDATE,
          entity: "proveedor",
          entityId: proveedor.id,
          entityName: proveedor.nombre || "",
          details: { acuerdo: nuevo, anterior: acuerdo, notas: notas.trim() || null },
        },
        userInfo,
        user
      );
      setEditando(false);
      setNotas("");
      showAlert("Acuerdo de comisión guardado", "success");
    } catch (err) {
      showAlert(err.message || "No se pudo guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-zinc-100 bg-white dark:bg-zinc-800/30 dark:border-zinc-700/40 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">Acuerdo de comisión</div>
            <div className="mt-1 text-lg font-bold text-zinc-900 dark:text-white">{describeAcuerdo(acuerdo)}</div>
            <p className="mt-1 text-xs text-zinc-500">
              Al ligar este planner a un evento se propone esta comisión; se puede ajustar para ese evento en concreto. Cambiarla no toca eventos anteriores.
            </p>
          </div>
          {!editando && !soloLectura && (
            <button
              type="button"
              onClick={() => { setTipo(acuerdo?.tipo || "porcentaje"); setValor(acuerdo ? String(acuerdo.valor) : ""); setEditando(true); }}
              className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition flex items-center gap-1.5"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" />
              {acuerdo ? "Cambiar" : "Definir"}
            </button>
          )}
        </div>
        {editando && !soloLectura && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Cómo cobra</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputClass}>
                {TIPOS_COMISION.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">{tipo === "fijo" ? "Monto por evento" : "Porcentaje"}</label>
              <div className="relative">
                <span className={`absolute ${tipo === "fijo" ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 text-sm text-zinc-400`}>{tipo === "fijo" ? "$" : "%"}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={tipo === "fijo" ? fmtMXNInput(valor) : valor}
                  onChange={(e) => setValor(tipo === "fijo" ? normalizeMoneyInput(e.target.value) : e.target.value.replace(/[^\d.]/g, ""))}
                  placeholder={tipo === "fijo" ? "15,000" : "10"}
                  className={`${inputClass} ${tipo === "fijo" ? "pl-7" : "pr-7"}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Nota del cambio (opcional)</label>
              <input type="text" value={notas} onChange={(e) => setNotas(e.target.value)} className={inputClass} placeholder="Renegociado en septiembre" />
            </div>
            <div className="sm:col-span-3 flex items-center gap-2">
              <button type="button" onClick={guardar} disabled={saving} className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                {saving ? "Guardando…" : "Guardar acuerdo"}
              </button>
              <button type="button" onClick={() => setEditando(false)} className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700">Cancelar</button>
              <span className="text-xs text-zinc-400 ml-auto">Con valor 0 se elimina el acuerdo.</span>
            </div>
          </div>
        )}
      </div>

      <HistorialPrecios
        pathSegments={["venues", selectedVenueId, "proveedores", proveedor?.id, "comisionHistorial"]}
        titulo="Historial del acuerdo"
        labels={{ inicial: "Acuerdo inicial", eliminado: "Acuerdo eliminado" }}
        vacioTexto="Todavía no hay cambios de acuerdo registrados."
        ocultarVacio
      />

      <div className="rounded-xl border border-zinc-100 bg-white dark:bg-zinc-800/30 dark:border-zinc-700/40 p-5">
        <div className="flex items-center gap-4 flex-wrap mb-3">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">Comisiones por evento</div>
          <div className="ml-auto flex items-center gap-4 text-sm">
            <span className="text-zinc-500">Pagadas: <span className="font-semibold text-emerald-600">{fmtMoney(pagadas)}</span></span>
            <span className="text-zinc-500">Por pagar: <span className="font-semibold text-amber-600">{fmtMoney(porPagar)}</span></span>
          </div>
        </div>
        {comisiones.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">Todavía no hay comisiones registradas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-400">
                  <th className="px-2 py-1 text-left font-medium">Evento</th>
                  <th className="px-2 py-1 text-left font-medium">Acuerdo aplicado</th>
                  <th className="px-2 py-1 text-right font-medium">Comisión</th>
                  <th className="px-2 py-1 text-left font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {comisiones.map(({ costo, evento }) => (
                  <tr key={costo.id}>
                    <td className="px-2 py-2">
                      {evento ? (
                        <button type="button" onClick={() => navigate(`/dashboard/eventos/${evento.id}?tab=operacion`, { state: { from: "proveedor", proveedorId: proveedor.id } })} className="font-medium text-zinc-900 dark:text-zinc-100 hover:underline text-left">
                          {evento.nombre || "Sin nombre"}
                        </button>
                      ) : (
                        <span className="text-zinc-400">Evento no encontrado</span>
                      )}
                      <div className="text-xs text-zinc-400">{formatCalendarDate(costo.fecha, calendarTimezone, { style: "short" }) || ""}</div>
                    </td>
                    <td className="px-2 py-2 text-xs text-zinc-500">{costo.comisionAcuerdo ? describeAcuerdo(costo.comisionAcuerdo) : "Capturada a mano"}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap font-medium text-zinc-900 dark:text-white">{fmtMoney(costo.monto)}</td>
                    <td className="px-2 py-2">
                      {costo.estado === "pagado" ? (
                        <>
                          <Badge color="emerald">Pagada {formatCalendarDate(costo.fechaPago, calendarTimezone, { style: "short" }) || ""}</Badge>
                          {costo.metodo && <div className="mt-0.5 text-xs text-zinc-500">{costo.metodo}</div>}
                        </>
                      ) : (
                        <Badge color="amber">Por pagar</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {sinComision.length > 0 && (
          <p className="mt-3 text-xs text-zinc-500">
            Es planner en {sinComision.length} {sinComision.length === 1 ? "evento" : "eventos"} sin comisión registrada; se captura desde el Balance de cada evento.
          </p>
        )}
      </div>
    </div>
  );
}
